import { Box, Button, Divider, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Tooltip } from "@powerdiary/components-library/lib";
import { useCallback, useRef, useState } from "react";
import { useAuthorizationContext, useClientPortalConfigContext } from "../../../context";
import { useClientPortalNavigate } from "hooks/useClientPortalRouter";
import { ROUTES } from "../../../routes";
import { LoginDialogMode } from "../../Account/Account.types";
import Login from "../../Account/LoginDialog/LoginDialog";
import styles from "./Header.module.css";
import HeaderDesktop from "./HeaderDesktop/HeaderDesktop";
import HeaderMobile from "./HeaderMobile";
import { useReturnUrl } from "hooks/useReturnUrl";

export default function Header() {
    const [showLoginDialog, setShowLoginDialog] = useState<LoginDialogMode | null>(null);
    const [bookingReturnUrl, setBookingReturnUrl] = useState<string | null>(null);
    const headerActionsRef = useRef<HTMLDivElement>(null);
    const { loggedIn } = useAuthorizationContext();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { clientPortalConfig } = useClientPortalConfigContext();
    const isClientPortalDisabled = clientPortalConfig?.isClientPortalDisabled ?? false;
    const registrationEnabled = clientPortalConfig?.newUsersCanRegister ?? true;
    const showExistingClientsOnlyTooltip = !registrationEnabled && !loggedIn;
    const mustLoginForBookingOrClasses =
        !loggedIn && ((clientPortalConfig?.areUsersRequiredToLoginFirst ?? true) || !registrationEnabled);

    const navigateTo = useClientPortalNavigate();
    const returnUrl = useReturnUrl();

    const handleBookingAction = useCallback(
        (route: string) => {
            if (mustLoginForBookingOrClasses) {
                setBookingReturnUrl(route);
                setShowLoginDialog(LoginDialogMode.Login);
            } else {
                navigateTo(route);
            }
        },
        [mustLoginForBookingOrClasses, navigateTo]
    );

    const handleLoginDialogClose = useCallback(() => {
        setShowLoginDialog(null);
        setBookingReturnUrl(null);
    }, []);

    const handleBrandClick = useCallback(() => {
        navigateTo(ROUTES.HOME.urlPath);
    }, [navigateTo]);

    const brandContent =
        clientPortalConfig.logoUrl === defaultLogoUrl ? (
            <Typography
                variant="heading4"
                className={styles.headerTitle}
                title={clientPortalConfig.clientPortalName}
                color={theme.palette.primary.main}
            >
                {clientPortalConfig.clientPortalName}
            </Typography>
        ) : (
            <Box data-test-id="header_logo">
                <img
                    data-test-id="image_header_logo"
                    src={clientPortalConfig.logoUrl}
                    alt={clientPortalConfig.clientPortalName}
                    className={styles.image}
                />
            </Box>
        );

    return (
        <Box component="header" sx={(theme) => ({ zIndex: theme.zIndex.appBar })} className={styles.header}>
            <Box className={styles.headerContent}>
                {isClientPortalDisabled ? (
                    <Box
                        data-test-id="header_brand"
                        sx={{ flex: 1, display: "flex", gap: 1, alignItems: "center", containerType: "inline-size" }}
                    >
                        {brandContent}
                    </Box>
                ) : (
                    <Box
                        component="button"
                        type="button"
                        data-test-id="header_brand"
                        className={styles.clickable}
                        sx={{ flex: 1, display: "flex", gap: 1, alignItems: "center", containerType: "inline-size" }}
                        onClick={handleBrandClick}
                        aria-label={clientPortalConfig.clientPortalName}
                    >
                        {brandContent}
                    </Box>
                )}
                {!isClientPortalDisabled && (
                    <div ref={headerActionsRef}>
                        {loggedIn ? (
                            isSmallScreen ? (
                                <HeaderMobile isLoggedIn={true} handleBookingAction={handleBookingAction} />
                            ) : (
                                <HeaderDesktop />
                            )
                        ) : isSmallScreen ? (
                            <HeaderMobile
                                isLoggedIn={false}
                                setShowLoginDialog={setShowLoginDialog}
                                setShowRegisterDialog={setShowLoginDialog}
                                handleBookingAction={handleBookingAction}
                            />
                        ) : (
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                    data-test-id="button_login"
                                    variant="outlined"
                                    onClick={() => setShowLoginDialog(LoginDialogMode.Login)}
                                >
                                    Log in
                                </Button>
                                <Tooltip
                                    title={
                                        showExistingClientsOnlyTooltip
                                            ? "This portal is available for existing clients only."
                                            : ""
                                    }
                                >
                                    <Box data-test-id="button_register_wrapper">
                                        <Button
                                            data-test-id="button_register"
                                            variant="contained"
                                            disabled={!registrationEnabled}
                                            onClick={() => setShowLoginDialog(LoginDialogMode.Register)}
                                        >
                                            Register
                                        </Button>
                                    </Box>
                                </Tooltip>
                            </Box>
                        )}
                    </div>
                )}
                {showLoginDialog && (
                    <Login
                        handleClose={handleLoginDialogClose}
                        returnUrl={bookingReturnUrl ?? returnUrl}
                        dialogMode={showLoginDialog}
                    />
                )}
            </Box>
            <Divider sx={{ width: "100%", borderBottomWidth: 2 }} />
        </Box>
    );
}

const defaultLogoUrl = "/images/logo/zanda-logo.svg";
